import { isCollageAnswer, type CollageAnswer } from './collage.js';

/**
 * What a customer already answered, carried onto another template's form.
 *
 * A product whose variations each have their own template (the six Mini Box
 * colours) swaps the whole form when the colour changes. Every colour asks the
 * same questions, but each has its own option set, so the ids differ — and
 * answers used to carry over only by id: fifteen uploaded photos and both
 * quotes were gone after one colour click. A question carries to the one on
 * the other form that is the same question: the same id, or else the same
 * type and the same label, when exactly one question on each side has it (two
 * "Tekst" fields are not guessed between).
 *
 * Shared by the storefront widget (a shopper switching colour) and the order
 * service (staff changing an ordered line's variation, order-variation.ts), so
 * a colour change carries exactly the same answers before and after checkout.
 */

/** The parts of a question carrying reads: the widget's OptionDef and the server's questions both fit. */
export interface CarryOption {
  id: string;
  type: string;
  label: string;
  config?: Record<string, unknown> | null;
  values: ReadonlyArray<{ id?: string; value: string }>;
}

const sameQuestion = (option: CarryOption): string => `${option.type}\u0000${option.label.trim().toLowerCase()}`;

/** Old option id → new option id, for every question the new form also asks. */
export function matchOptions(from: readonly CarryOption[], to: readonly CarryOption[]): Map<string, string> {
  const map = new Map<string, string>();
  const toIds = new Set(to.map((o) => o.id));
  for (const option of from) if (toIds.has(option.id)) map.set(option.id, option.id);
  const counts = (list: readonly CarryOption[]) => {
    const out = new Map<string, number>();
    for (const option of list) out.set(sameQuestion(option), (out.get(sameQuestion(option)) ?? 0) + 1);
    return out;
  };
  const fromCounts = counts(from);
  const toCounts = counts(to);
  const toByKey = new Map(to.map((o) => [sameQuestion(o), o]));
  const taken = new Set(map.values());
  for (const option of from) {
    if (map.has(option.id)) continue;
    const key = sameQuestion(option);
    if (fromCounts.get(key) !== 1 || toCounts.get(key) !== 1) continue;
    const target = toByKey.get(key)!;
    if (taken.has(target.id)) continue;
    map.set(option.id, target.id);
    taken.add(target.id);
  }
  return map;
}

/** The answers, under the new form's ids, translated where an answer names something of the old form. */
export function carryAnswers(
  from: readonly CarryOption[],
  to: readonly CarryOption[],
  values: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  return carryReport(from, to, values).values;
}

export interface CarryReport {
  /** The carried answers, keyed by the new form's ids. */
  values: Record<string, unknown>;
  /** Old ids whose answer carried. */
  carried: string[];
  /**
   * Old ids that had an answer and lost it: the new form does not ask the
   * question, or not with that choice (a value it does not offer).
   */
  dropped: string[];
}

/** {@link carryAnswers}, saying which answers made it and which did not. */
export function carryReport(
  from: readonly CarryOption[],
  to: readonly CarryOption[],
  values: Readonly<Record<string, unknown>>,
): CarryReport {
  const fromById = new Map(from.map((o) => [o.id, o]));
  const toById = new Map(to.map((o) => [o.id, o]));
  const map = matchOptions(from, to);
  const out: Record<string, unknown> = {};
  const carried: string[] = [];
  const dropped: string[] = [];
  for (const option of from) {
    const answer = values[option.id];
    if (answer === undefined) continue;
    const newId = map.get(option.id);
    const moved =
      newId === undefined
        ? undefined
        : option.id === newId
          ? answer
          : translate(fromById.get(option.id)!, toById.get(newId)!, answer);
    if (newId !== undefined && moved !== undefined) out[newId] = moved;
    // Only a real answer is worth reporting: an emptied field lost nothing.
    if (!answered(answer)) continue;
    if (newId !== undefined && moved !== undefined) carried.push(option.id);
    else dropped.push(option.id);
  }
  return { values: out, carried, dropped };
}

/** Re-keys a record or map kept by option id (collage media, photos still uploading). */
export function rekeyByOption<T>(kept: Record<string, T> | undefined, map: Map<string, string>): Record<string, T> {
  const out: Record<string, T> = {};
  for (const [id, value] of Object.entries(kept ?? {})) {
    const next = map.get(id);
    if (next) out[next] = value;
  }
  return out;
}

const answered = (answer: unknown): boolean =>
  answer !== undefined && answer !== null && answer !== '' && !(Array.isArray(answer) && answer.length === 0);

function translate(from: CarryOption, to: CarryOption, answer: unknown): unknown {
  // A collage's slots are the old template's layers: Slika N goes to Slika N.
  if (isCollageAnswer(answer)) return moveCollage(answer, slotsOf(from), slotsOf(to));
  // A choice by the value it stands for, not the old form's value id.
  if (from.values.length > 0 || to.values.length > 0) {
    const pick = (picked: unknown): unknown => {
      const was = from.values.find((v) => (v.id !== undefined && v.id === picked) || v.value === picked);
      const now = was ? to.values.find((v) => v.value === was.value) : undefined;
      if (!was || !now) return undefined;
      return was.id !== undefined && picked === was.id ? (now.id ?? now.value) : now.value;
    };
    if (Array.isArray(answer)) {
      const picked = answer.map(pick).filter((v) => v !== undefined);
      return picked.length > 0 ? picked : undefined;
    }
    return pick(answer);
  }
  return answer;
}

const slotsOf = (option: CarryOption): string[] =>
  Array.isArray(option.config?.targetElementIds)
    ? (option.config.targetElementIds as unknown[]).filter((id): id is string => typeof id === 'string')
    : [];

function moveCollage(answer: CollageAnswer, from: string[], to: string[]): CollageAnswer {
  const slots: CollageAnswer['slots'] = {};
  for (const [elementId, fill] of Object.entries(answer.slots)) {
    const index = from.indexOf(elementId);
    const target = index >= 0 ? to[index] : undefined;
    // A slot the other template does not have leaves its photo in the pool, as a spare.
    if (target) slots[target] = fill;
  }
  return { ...answer, slots };
}
